import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useNavigate as useRouterNavigate } from "react-router-dom";
import { useFunnelMode } from "./funnel-mode";

/** The funnel's X returns to the app dashboard (the funnel is always authenticated). */
export const HOME_PATH = "/pages/home";

export const HELP_URL = "https://help.storydoc.com";

export const openHelpCenter = () => {
  window.open(HELP_URL, "_blank", "noopener,noreferrer");
};

interface FunnelLocation {
  pathname: string;
  state: unknown;
}

interface NavigateOptions {
  state?: unknown;
  replace?: boolean;
}

interface FunnelNavigation {
  location: FunnelLocation;
  /** react-router-compatible: `navigate(path, { state, replace })` or `navigate(-1)`. */
  navigate: (to: string | number, options?: NavigateOptions) => void;
  /** False on the funnel's first step, where `navigate(-1)` would be a no-op. */
  canGoBack: boolean;
}

const FunnelNavigationContext = createContext<FunnelNavigation | null>(null);

/** In-memory history for the funnel: same navigate/location surface as react-router, but no URL changes. */
export const FunnelNavigationProvider = ({
  initialPath,
  initialState = null,
  children,
}: {
  initialPath: string;
  initialState?: unknown;
  children: ReactNode;
}) => {
  const [history, setHistory] = useState({
    stack: [{ pathname: initialPath, state: initialState }],
    index: 0,
  });

  const navigate = useCallback(
    (to: string | number, options: NavigateOptions = {}) => {
      setHistory(({ stack, index }) => {
        if (typeof to === "number") {
          const next = Math.max(0, Math.min(stack.length - 1, index + to));
          return { stack, index: next };
        }
        const entry = { pathname: to, state: options.state ?? null };
        if (options.replace) {
          const replaced = [...stack];
          replaced[index] = entry;
          return { stack: replaced, index };
        }
        const truncated = stack.slice(0, index + 1);
        truncated.push(entry);
        return { stack: truncated, index: index + 1 };
      });
    },
    [],
  );

  const value = useMemo<FunnelNavigation>(
    () => ({
      location: history.stack[history.index],
      navigate,
      canGoBack: history.index > 0,
    }),
    [history, navigate],
  );

  return (
    <FunnelNavigationContext.Provider value={value}>
      {children}
    </FunnelNavigationContext.Provider>
  );
};

const useFunnelNavigation = () => {
  const context = useContext(FunnelNavigationContext);
  if (!context) {
    throw new Error("Funnel navigation hooks must be used inside the funnel.");
  }
  return context;
};

/** Drop-in for react-router's `useNavigate`, scoped to the funnel's memory history. */
export const useNavigate = () => useFunnelNavigation().navigate;

/** Drop-in for react-router's `useLocation`, scoped to the funnel's memory history. */
export const useLocation = () => useFunnelNavigation().location;

/**
 * Go-back handler for the funnel's step headers. Steps back through the
 * in-memory history; on the first step — where there's nothing to go back
 * to — it exits instead: closes the platform overlay, or returns the signup
 * page to the dashboard.
 */
export const useFunnelBack = () => {
  const { navigate, canGoBack } = useFunnelNavigation();
  const { mode, onClose } = useFunnelMode();
  const routerNavigate = useRouterNavigate();

  return useCallback(() => {
    if (canGoBack) {
      navigate(-1);
      return;
    }
    if (mode === "platform") {
      onClose?.();
      return;
    }
    routerNavigate(HOME_PATH);
  }, [canGoBack, navigate, mode, onClose, routerNavigate]);
};
